import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Checkbox, Col, DatePicker, Input, Row, Space } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { dateToDayjs } from '@/utils';
import {
  useGetAdoptionLiaisonsChildQuery,
  useGetCaseWorkersQuery,
  useGetChildTaskStatusQuery,
  useGetChildTaskTypesQuery,
  useGetCountiesQuery,
  useGetGendersQuery,
  useGetNameSuffixesQuery,
  useGetRacesQuery,
  useGetWWKRecruitersQuery,
  useGetRegionalRecruitmentCoordinatorQuery,
  useGetFCMSupervisorQuery,
} from '@/services/general-api';
import { useAddChildTaskMutation, useSaveChildMutation } from '@/services/child-api';
import { BriefcaseSvg, CheckSvg, FileAltSvg, PlusSvg, SaveSvg, UserSvg } from '@/assets/icons';
import Select from '@/components/select';
import Panel from '@/components/panel';
import FixedFooter from '@/components/fixed-footer';
import Button from '@/components/button';
import { Form } from './styles';
import AddModal from './add-modal';
import AddModalFCMS from './add-modal-fcms';
import { selectorPermissions } from '@/stores/auth';

const { TextArea } = Input;

const EditForm = ({ childData }) => {
  const navigate = useNavigate();

  const { data: suffixData, isLoading: loadingSuffixData } = useGetNameSuffixesQuery();
  const { data: genderData, isLoading: loadingGenderData } = useGetGendersQuery();
  const { data: raceData, isLoading: loadingRaceData } = useGetRacesQuery();
  const { data: countryData, isLoading: loadingCountryData } = useGetCountiesQuery();
  const { data: liaisonData, isLoading: loadingLiaisonData } = useGetAdoptionLiaisonsChildQuery();
  const { data: rrcData, isLoading: loadingrrcData } = useGetRegionalRecruitmentCoordinatorQuery();
  //const { data: fcmsData, isLoading: loadingfcmsData } = useGetFCMSupervisorQuery();
  const { data: fcmsData, refetch: refetchFcms, isLoading: loadingfcmsData } = useGetFCMSupervisorQuery();
  const { data: recruiterData, isLoading: loadingRecruiterData } = useGetWWKRecruitersQuery();
  const { data: workerData, refetch: refetchWorkers, isLoading: loadingWorkerData } = useGetCaseWorkersQuery();
  const { data: taskTypeData, isLoading: loadingTaskTypeData } = useGetChildTaskTypesQuery();
  const { data: taskStatusData, isLoading: loadingTaskStatusData } = useGetChildTaskStatusQuery();
  const [saveChild, { isLoading: savingChild }] = useSaveChildMutation();
  const [addTask, { isLoading: addingTask }] = useAddChildTaskMutation();

  const [isAddModalOpen, openAddModal] = useState(false);
  const [isAddModalFCMSOpen, openAddModalFCMS] = useState(false);

  const [form] = Form.useForm();

  const initialValues = useMemo(() => {
    if (!childData) return {
      regionalRecruitmentCoordinator: {
        personId: 29323 // default value
      }
    };
    const values = _.cloneDeep(childData);
    values.birthInfo.date = dateToDayjs(values.birthInfo.date);
    values.referral = dateToDayjs(values.referral);
    values.dateMatchedWithAdoptiveFamily = dateToDayjs(values.dateMatchedWithAdoptiveFamily);
    values.dateAdoptionWasFinalized = dateToDayjs(values.dateAdoptionWasFinalized);

    //Set default personId if missing
    if (!values.regionalRecruitmentCoordinator) {
      values.regionalRecruitmentCoordinator = { personId: 29323 };
    } else if (!values.regionalRecruitmentCoordinator.personId) {
      values.regionalRecruitmentCoordinator.personId = 29323;
    }

    return values;
  }, [childData]);

  const onFinish = async (values) => {
    const body = _.omit(values, ['task']);
    body.birthInfo.date = body.birthInfo.date.toISOString();
    if (body.newToRecruitment) body.newToRecruitmentDate = new Date().toISOString();
    body.referral = body.referral.toISOString();
    
    try {
      body.dateMatchedWithAdoptiveFamily = body.dateMatchedWithAdoptiveFamily.toISOString();
    }
    catch(err) {
      
    }

    try {
      body.dateAdoptionWasFinalized = body.dateAdoptionWasFinalized.toISOString();
    }
    catch(err) {
      
    }

    try {
      const res = await saveChild({ childId: childData?.childId, body }).unwrap();

      if (values.task?.type.id) {
        await addTask({ childId: res.childId, body: [values.task] }).unwrap();
      }

      navigate(`/child/${res.childId}/view`);
    } catch (err) { }
  };

  const permissions = useSelector(selectorPermissions);
  const fReadOnly = (permissions.adminStaffAndSuper === false);

  const getDisplayMode = () => {
    if(permissions.adminStaffAndSuper === false) {
      return 'none';
      
    } else {
      //console.log("dmode:block");
    }
  }

  const displayMode = getDisplayMode();

  return (
    <>
      <Form layout="vertical" form={form} initialValues={initialValues} onFinish={onFinish}>
        <Panel icon={<UserSvg />} title="Child" dv={displayMode} >
          <Row gutter={32}>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['name', 'first']} label="First Name" rules={[{ required: true }]}>
                <Input readOnly={fReadOnly} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['name', 'last']} label="Last Name" rules={[{ required: true }]}>
                <Input readOnly={fReadOnly} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['name', 'suffix']} label="Suffix">
                <Select readOnly={fReadOnly} options={suffixData} valueName="code" loading={loadingSuffixData} allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['birthInfo', 'date']} label="Birth Date" rules={[{ required: true }]}>
                <DatePicker readOnly={fReadOnly}
                  format="MM/DD/YYYY"
                  onBlur={(e) => {
                    const inputVal = e.target.value;
                    const validDate = moment(inputVal, ['M/D/YYYY', 'MM/DD/YYYY'], true);
                    if (validDate.isValid()) {
                      form.setFieldsValue({
                        birthInfo: { date: validDate },
                      });
                    } else {
                      form.setFieldsValue({
                        birthInfo: { date: null },
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['gender', 'id']} label="Gender" rules={[{ required: true }]}>
                <Select readOnly={fReadOnly}
                  options={genderData}
                  loading={loadingGenderData}
                  filterOption={(input, option) => option?.code?.startsWith(input.toLowerCase())}
                  showSearch
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['race', 'id']} label="Race/Ethnicity" rules={[{ required: true }]}>
                <Select readOnly={fReadOnly}
                  options={raceData}
                  loading={loadingRaceData}
                  filterOption={(input, option) => option.name.toLowerCase().startsWith(input.toLowerCase())}
                  showSearch
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12} xl={8}>
              <Form.Item name="dateMatchedWithAdoptiveFamily" label="Date Matched With Adoptive Family">
              <DatePicker 
                  format="MM/DD/YYYY"
                  onBlur={(e) => {
                    const inputVal = e.target.value;
                    const validDate = moment(inputVal, ['M/D/YYYY', 'MM/DD/YYYY'], true);
                    if (validDate.isValid()) {
                      form.setFieldsValue({
                        dateMatchedWithAdoptiveFamily: validDate,
                      });
                    } else {
                      form.setFieldsValue({
                        dateMatchedWithAdoptiveFamily: null,
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12} xl={8}>
              <Form.Item name="dateAdoptionWasFinalized" label="Date Adoption Was Finalized">
              <DatePicker 
                  format="MM/DD/YYYY"
                  onBlur={(e) => {
                    const inputVal = e.target.value;
                    const validDate = moment(inputVal, ['M/D/YYYY', 'MM/DD/YYYY'], true);
                    if (validDate.isValid()) {
                      form.setFieldsValue({
                        dateAdoptionWasFinalized: validDate,
                      });
                    } else {
                      form.setFieldsValue({
                        dateAdoptionWasFinalized: null,
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12} xl={8}>
              <Form.Item name="newToRecruitment" valuePropName="checked">
                <Checkbox readOnly={fReadOnly}>New To Recruitment</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel icon={<BriefcaseSvg />} title="Liaison" dv={displayMode}>
          <Row gutter={32}>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name="referral" label="Initial Referral Date" rules={[{ required: true }]}>
                <DatePicker readOnly={fReadOnly}
                  format="MM/DD/YYYY"
                  onBlur={(e) => {
                    const inputVal = e.target.value;
                    const validDate = moment(inputVal, ['M/D/YYYY', 'MM/DD/YYYY'], true);
                    if (validDate.isValid()) {
                      form.setFieldsValue({
                        referral: validDate,
                      });
                    } else {
                      form.setFieldsValue({
                        referral: null,
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8} style={{ display: 'flex', gap: 16 }}>
              <Form.Item name={['county', 'id']} label="County" rules={[{ required: true }]} style={{ flex: 1 }}>
                <Select readOnly={fReadOnly}
                  options={countryData}
                  loading={loadingCountryData}
                  showSearch
                  filterOption={(input, option) => option.name.toLowerCase().startsWith(input.toLowerCase())}
                  onChange={(id) => {
                    const personId = _.find(countryData, { id }).adoptionLiaison.personId;
                    const personExists = liaisonData.some((option) => option.id === personId);

                    const rrcpersonId = _.find(countryData, { id }).regionalRecruitmentCoordinator.personId;
                    const rrcpersonExists = rrcData.some((option) => option.id === rrcpersonId);

                    if (personExists) {
                      form.setFieldValue(['adoptionLiaison', 'personId'], personId);
                    } else {
                      form.setFieldValue(['adoptionLiaison', 'personId'], undefined);
                    }

                    if(rrcpersonExists) {
                      //form.setFieldValue(['regionalRecruitmentCoordinator', 'personId'], rrcpersonId);
                    } else {
                      //form.setFieldValue(['regionalRecruitmentCoordinator', 'personId'], undefined);
                    }
                    
                  }}
                />
              </Form.Item>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.county?.id !== currentValues.county?.id}
              >
                {({ getFieldValue }) => {
                  const id = getFieldValue('county')?.id;
                  const country = _.find(countryData, { id });
                  return (
                    <Form.Item label=" " style={{ width: 70 }}>
                      {country?.region.name}
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['adoptionLiaison', 'personId']} label="Adoption Liaison" rules={[{ required: true }]}>
                <Select readOnly={fReadOnly} options={liaisonData} loading={loadingLiaisonData} showSearch />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Row>
                <Col>
                  <Form.Item
                    name={['caseworker', 'personId']}
                    label="Caseworker"
                    rules={[{ required: true, message: 'Please select a Caseworker' }]}
                  >
                    <Select readOnly={fReadOnly} options={workerData} loading={loadingWorkerData} style={{ width: 300 }} showSearch />
                  </Form.Item>
                </Col>
                <Col>
                  <Button icon={<PlusSvg />} onClick={() => openAddModal(true)} style={{ marginTop: 30 }} />
                </Col>
              </Row>
            </Col>
            <Col xs={24} md={12} xl={8} style={{ display: 'none' }}>
              <Form.Item name={['wwkRecruiter', 'personId']} label="WWK Recruiter">
                <Select readOnly={fReadOnly} options={recruiterData} loading={loadingRecruiterData} allowClear showSearch />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['magikEmail', 'emailAddress']} label="MaGIK Email" rules={[{ type: 'email' }]}>
                <Input readOnly={fReadOnly} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item name={['regionalRecruitmentCoordinator', 'personId']} label="Assessor / CS Writer" rules={[{ required: false }]}>
                <Select readOnly={fReadOnly} options={rrcData} loading={loadingrrcData} showSearch />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
                <Row>
                  <Col>
                     <Form.Item name={['fcmSupervisor', 'personId']} label="FCM Supervisor" rules={[{ required: false }]}>
                      <Select style={{width:200}} readOnly={fReadOnly} options={fcmsData} loading={loadingfcmsData} showSearch />
                    </Form.Item>
                  </Col>
                  <Col>
                    <Button icon={<PlusSvg />} onClick={() => openAddModalFCMS(true)} style={{ marginTop: 30 }} />           
                </Col>
 
                </Row>
    
            </Col>
          </Row>
         
        </Panel>

        {!childData && (
          <Panel icon={<BriefcaseSvg />} title="Task">
            <Row gutter={32}>
              <Col xs={24} md={12} xl={8}>
                <Form.Item name={['task', 'type', 'id']} label="Type">
                  <Select readOnly={fReadOnly} options={taskTypeData} loading={loadingTaskTypeData} allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} md={12} xl={8}>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.task.type?.id !== currentValues.task.type?.id}
                >
                  {({ getFieldValue }) => {
                    const typeId = getFieldValue('task')?.type?.id;
                    if (!typeId) {
                      form.setFieldValue(['task', 'status', 'id'], undefined);
                      return null;
                    }
                    return (
                      <Form.Item name={['task', 'status', 'id']} label="Status" rules={[{ required: true }]}>
                        <Select readOnly={fReadOnly} options={taskStatusData} loading={loadingTaskStatusData} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>
            </Row>
          </Panel>
        )}

        <Panel icon={<FileAltSvg />} title="Bio">
          <Form.Item name="bio" label="Bio">
            <TextArea readOnly={fReadOnly} autoSize={{ minRows: 2 }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <TextArea autoSize={{ minRows: 2 }} />
          </Form.Item>
        </Panel>

        <Panel icon={<CheckSvg />} title="Flags" dv={displayMode} style={{ marginBottom: 58 }}>
          <Row gutter={[24, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="middle">
                <div>
                  <div style={{ marginBottom: 4 }}>Video</div>
                  <Form.Item name="hasWNDU_Video" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>Has WNDU segment</Checkbox>
                  </Form.Item>
                  <Form.Item name="hasAKB_Video" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>Has AKB video</Checkbox>
                  </Form.Item>
                  <Form.Item name="hasGMH_Video" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>Has GMH video</Checkbox>
                  </Form.Item>
                  <Form.Item name="videoAiredOnWTHI" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>AKB Video aired on WTHI</Checkbox>
                  </Form.Item>
                  <Form.Item name="videoAiredOnWLKY" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>AKB Video aired on WLKY</Checkbox>
                  </Form.Item>
                 
                  <Form.Item name="akbVideoAiredOnWNDU" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>AKB Video aired on WNDU</Checkbox>
                  </Form.Item>

                  <Form.Item name="akbVideoShownAtChurch" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>AKB Video shown at Church</Checkbox>
                  </Form.Item>
                </div>

                <div>
                  <div style={{ marginBottom: 4 }}>Case Management</div>
                  <Form.Item name="assignedToOYCM" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>Assigned to OYCM</Checkbox>
                  </Form.Item>
                  <Form.Item name="planChange" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>Plan change + keep active in recruitment</Checkbox>
                  </Form.Item>
                  <Form.Item name="includeOnAUK" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>Include on AUK</Checkbox>
                  </Form.Item>
                  <Form.Item name="doNotIncludeOnAUK" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>Do not include on AUK - will not consider OOS</Checkbox>
                  </Form.Item>
                </div>

                <div>
                  <div style={{ marginBottom: 4 }}>Gender</div>
                  <Form.Item name="lgbtqPlus" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>LGBTQ+</Checkbox>
                  </Form.Item>
                  <Form.Item name="nonBinaryGenderFluid" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>Non-binary/gender-fluid</Checkbox>
                  </Form.Item>
                </div>

                <div>
                  <div style={{ marginBottom: 4 }}>Risk Factors</div>
                  <Form.Item name="sexualAbuse" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>Sexual abuse</Checkbox>
                  </Form.Item>
                  <Form.Item name="historyOfMultiplePlacement" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>History of multiple placements</Checkbox>
                  </Form.Item>
                  <Form.Item name="previouslyAdopted" valuePropName="checked" className="m-0">
                    <Checkbox readOnly={fReadOnly}>Previously Adopted</Checkbox>
                  </Form.Item>
                </div>
              </Space>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 4 }}>Disabilities/Needs</div>
              <Form.Item name="autism" valuePropName="checked" className="m-0">
                <Checkbox readOnly={fReadOnly}>Autism</Checkbox>
              </Form.Item>
              <Form.Item name="nonVerbal" valuePropName="checked" className="m-0">
                <Checkbox readOnly={fReadOnly}>Non-verbal</Checkbox>
              </Form.Item>
              <Form.Item name="moderateOrSeverMedical" valuePropName="checked" className="m-0">
                <Checkbox>Moderate or severe medical</Checkbox>
              </Form.Item>
              <Form.Item name="moderateOrSeverDevelopmental" valuePropName="checked" className="m-0">
                <Checkbox readOnly={fReadOnly}>Moderate or severe developmental</Checkbox>
              </Form.Item>
              <Form.Item name="moderateEmotional" valuePropName="checked" className="m-0">
                <Checkbox readOnly={fReadOnly}>Moderate emotional</Checkbox>
              </Form.Item>
              <Form.Item name="severeEmotional" valuePropName="checked" className="m-0">
                <Checkbox readOnly={fReadOnly}>Severe emotional</Checkbox>
              </Form.Item>
              <Form.Item name="moderateBehavioral" valuePropName="checked" className="m-0">
                <Checkbox readOnly={fReadOnly}>Moderate behavioral</Checkbox>
              </Form.Item>
              <Form.Item name="severeBehavioral" valuePropName="checked" className="m-0">
                <Checkbox readOnly={fReadOnly}>Severe behavioral</Checkbox>
              </Form.Item>
              <Form.Item name="moderateOrSevereLearningIntellectual" valuePropName="checked" className="m-0">
                <Checkbox readOnly={fReadOnly}>Moderate or severe learning/intellectual</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="other" label="Other">
            <TextArea readOnly={fReadOnly} autoSize={{ minRows: 2 }} />
          </Form.Item>
        </Panel>

        <FixedFooter>
          <Button type="primary" htmlType="submit" icon={<SaveSvg />} loading={savingChild || addingTask}>
            Save
          </Button>
        </FixedFooter>
      </Form>
      <AddModal suffixData={suffixData} open={isAddModalOpen} refetch={refetchWorkers} onClose={() => openAddModal(false)} />
      <AddModalFCMS suffixData={suffixData} open={isAddModalFCMSOpen} refetch={refetchFcms} onClose={() => openAddModalFCMS(false)} />
    </>
  );
};

export default EditForm;
